import YAML from 'yaml'
import { normalizeJsonObject, type JsonObject } from '../shared/jsonValues'

type LeadingComment = {
  body: string
  rest: string
}

const stripBom = (value: string) => value.replace(/^\uFEFF/, '')

const stripCommonIndentation = (value: string) => {
  const lines = value.replace(/\r/g, '').split('\n')
  const contentLines = lines.filter((line) => line.trim().length > 0)
  if (contentLines.length === 0) return ''

  const smallestIndent = contentLines.reduce((currentSmallest, line) => {
    const indent = line.match(/^\s*/)?.[0].length ?? 0
    return Math.min(currentSmallest, indent)
  }, Number.POSITIVE_INFINITY)

  return lines
    .map((line) => {
      if (line.trim().length === 0) return ''
      return line.slice(smallestIndent)
    })
    .join('\n')
    .trim()
}

const takeLeadingBlockComment = (value: string): LeadingComment | null => {
  const trimmed = value.trimStart()
  if (!trimmed.startsWith('/*')) return null

  const commentEnd = trimmed.indexOf('*/')
  if (commentEnd < 0) return null

  return {
    body: trimmed.slice(2, commentEnd),
    rest: trimmed.slice(commentEnd + 2),
  }
}

export const extractFeaturePreviewParams = (content: string): JsonObject => {
  let source = stripBom(String(content || ''))

  while (source.trimStart().startsWith('/*')) {
    const comment = takeLeadingBlockComment(source)
    if (!comment) return {}

    const body = stripCommonIndentation(comment.body)
    if (body.startsWith('Params:')) {
      const rawParams = body.slice('Params:'.length).trim()
      if (!rawParams) return {}

      try {
        const parsed = YAML.parse(rawParams) as Record<string, unknown> | null
        return normalizeJsonObject(parsed)
      } catch {
        return {}
      }
    }

    source = comment.rest
  }

  return {}
}

export default extractFeaturePreviewParams
